import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useReveal } from '../../hooks/useReveal';
import { ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const { t, isRTL, language } = useLanguage();
  const [ref, visible] = useReveal();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const lang = language === 'ar' ? 'ar' : 'en';

  useEffect(() => {
    axios.get(`${API}/api/products`).then(r => setProducts(r.data.slice(0, 6))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  if (!loading && products.length === 0) return null;

  return (
    <section id="products" ref={ref} className="py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628]">{t('products.title')}</h2>
          <div className="w-16 h-1 bg-[#00B4D8] mx-auto mt-6 rounded-full" />
          <p className="text-base text-slate-500 mt-6 max-w-2xl mx-auto">{t('products.subtitle')}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#00B4D8] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => {
              const name = p[`name_${lang}`] || p.name_ar || '';
              const desc = p[`short_desc_${lang}`] || p.short_desc_ar || '';
              const features = p[`features_${lang}`] || p.features_ar || [];
              return (
                <div key={p.id} data-testid={`product-card-${i}`}
                  className={`bg-white border border-slate-100 rounded-xl overflow-hidden hover:shadow-lg hover:border-[#00B4D8]/20 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: visible ? `${100 + i * 80}ms` : '0ms' }}>
                  <div className="aspect-[16/9] bg-[#F1F5F9] overflow-hidden relative">
                    {p.image ? (
                      <img src={p.image.startsWith('http') ? p.image : `${API}/api/files/${p.image}`} alt={name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Sparkles className="w-10 h-10 text-slate-200" /></div>
                    )}
                  </div>
                  <div className="p-6 space-y-3">
                    <h3 className="text-lg font-bold text-[#0A1628]">{name}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{desc}</p>
                    {features.length > 0 && (
                      <ul className="space-y-1.5">
                        {features.slice(0, 3).map((f, fi) => (
                          <li key={fi} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00B4D8] mt-1.5 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                    )}
                    <a href={`/products/${p.slug}`} className="inline-flex items-center gap-2 text-sm font-semibold text-[#00B4D8] hover:text-[#0094B3] transition-colors pt-2 group/btn">
                      {t('products.viewDetails')}
                      <Arrow className="w-4 h-4 group-hover/btn:translate-x-1 rtl:group-hover/btn:-translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {products.length > 0 && (
          <div className={`text-center mt-12 transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}>
            <a href="/products" className="inline-flex items-center gap-2 bg-[#0A1628] hover:bg-[#132240] text-white font-medium px-7 py-3 rounded-lg transition-colors text-sm">
              {t('products.viewAll')} <Arrow className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default Products;
